import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import PrivateReplyModal from './PrivateReplyModal';
import { notify, confirmAsync } from '../utils/notify';
import './CommentSettings.css';

/**
 * PostComments — Modal overlay that lists Instagram comments for a published post.
 * Light theme, Instagram-style layout with avatars and threaded AI replies.
 *
 * Props:
 *   postId            — ScheduledPost.id
 *   postCaption       — short caption preview for the header
 *   initialAiPaused   — current AI pause state from parent
 *   onClose           — close modal callback
 */
function PostComments({ postId, postCaption, initialAiPaused = false, onClose }) {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [replyTexts, setReplyTexts] = useState({});
  const [replying, setReplying] = useState({});

  // New comment as owner
  const [newCommentText, setNewCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  // Delete state
  const [deletingId, setDeletingId] = useState(null);
  const [deletingAiReplyId, setDeletingAiReplyId] = useState(null);

  // Edit state (owner comments only)
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // AI pause state
  const [aiPaused, setAiPaused] = useState(initialAiPaused);
  const [togglingAi, setTogglingAi] = useState(false);

  // Private Reply modal state (admin-only DM fan-out on a public comment)
  const [privateReplyTarget, setPrivateReplyTarget] = useState(null);

  // Ref for auto-scroll to bottom
  const commentsEndRef = useRef(null);
  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /* ── helpers ── */
  const getInitial = (username = '') => {
    const clean = username.replace(/^@/, '');
    return clean.charAt(0).toUpperCase() || '?';
  };

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all' && filter !== 'unreplied') {
        params.sentiment = filter;
      }
      const response = await apiClient.get(
        `/api/v1/comments/posts/${postId}/comments`,
        { params }
      );
      let data = response.data;

      if (filter === 'unreplied') {
        data = data.filter((c) => !c.replied_at);
      }

      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  }, [postId, filter]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  /* ── handlers ── */
  const handleReply = async (commentId) => {
    const message = (replyTexts[commentId] || '').trim();
    if (!message) return;

    setReplying((prev) => ({ ...prev, [commentId]: true }));
    try {
      await apiClient.post(
        `/api/v1/comments/posts/${postId}/comments/${commentId}/reply`,
        { message }
      );
      setReplyTexts((prev) => ({ ...prev, [commentId]: '' }));
      await fetchComments();
    } catch (err) {
      console.error('Reply error:', err);
    } finally {
      setReplying((prev) => ({ ...prev, [commentId]: false }));
    }
  };

  const handlePostNewComment = async () => {
    const message = newCommentText.trim();
    if (!message) return;

    setPostingComment(true);
    try {
      await apiClient.post(
        `/api/v1/comments/posts/${postId}/comments/new`,
        { message }
      );
      setNewCommentText('');
      await fetchComments();
      // Scroll to bottom to show new comment
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Post comment error:', err);
      notify.error(err.response?.data?.detail || t('comments.replyError'));
    } finally {
      setPostingComment(false);
    }
  };

  const handleDelete = async (comment) => {
    const isOwner = !!comment.is_owner_comment;
    const confirmKey = isOwner ? 'comments.confirmDelete' : 'comments.confirmModerate';
    const fallbackMsg = isOwner
      ? 'Are you sure you want to delete this comment?'
      : `Remove @${comment.external_username || 'user'}'s comment from the public post?`;
    if (!(await confirmAsync({ message: t(confirmKey, { username: comment.external_username, defaultValue: fallbackMsg }), danger: true }))) return;

    setDeletingId(comment.id);
    try {
      await apiClient.delete(
        `/api/v1/comments/posts/${postId}/comments/${comment.id}`
      );
      await fetchComments();
    } catch (err) {
      console.error('Delete comment error:', err);
      notify.error(err.response?.data?.detail || t('comments.deleteError'));
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditText(comment.content || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveEdit = async (commentId) => {
    const message = editText.trim();
    if (!message) return;

    if (!(await confirmAsync({ message: t('comments.confirmEdit') }))) return;

    setSavingEdit(true);
    try {
      await apiClient.put(
        `/api/v1/comments/posts/${postId}/comments/${commentId}`,
        { message }
      );
      setEditingId(null);
      setEditText('');
      await fetchComments();
    } catch (err) {
      console.error('Edit comment error:', err);
      notify.error(err.response?.data?.detail || t('comments.editError'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleAiPause = async () => {
    setTogglingAi(true);
    try {
      await apiClient.post(`/api/v1/posts/posts/${postId}/ai-pause`, {
        paused: !aiPaused,
      });
      setAiPaused(!aiPaused);
    } catch (err) {
      console.error('AI pause toggle error:', err);
    } finally {
      setTogglingAi(false);
    }
  };

  const handleDeleteAiReply = async (commentId) => {
    if (!(await confirmAsync({ message: t('comments.confirmDeleteAiReply'), danger: true }))) return;

    setDeletingAiReplyId(commentId);
    try {
      await apiClient.delete(
        `/api/v1/comments/posts/${postId}/comments/${commentId}/ai-reply`
      );
      await fetchComments();
    } catch (err) {
      console.error('Delete AI reply error:', err);
      notify.error(err.response?.data?.detail || t('comments.deleteError'));
    } finally {
      setDeletingAiReplyId(null);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filters = [
    { key: 'all', label: t('comments.filterAll') },
    { key: 'positive', label: t('comments.filterPositive') },
    { key: 'neutral', label: t('comments.filterNeutral') },
    { key: 'negative', label: t('comments.filterNegative') },
    { key: 'unreplied', label: t('comments.filterUnreplied') },
  ];

  /* ── render ── */
  return (
    <div className="post-comments-overlay" onClick={onClose}>
      <div className="post-comments-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="pcm-header">
          <h3>
            {t('comments.commentsFor')}{' '}
            <span className="pcm-header-caption">
              {(postCaption || '').slice(0, 40)}
              {(postCaption || '').length > 40 ? '…' : ''}
            </span>
          </h3>
          <button className="pcm-close-btn" onClick={onClose}>✕</button>
        </div>

        {/* AI Pause Toggle */}
        <div className="pcm-ai-toggle">
          <button
            className={`pcm-ai-toggle-btn ${aiPaused ? 'paused' : 'active'}`}
            onClick={handleToggleAiPause}
            disabled={togglingAi}
          >
            {aiPaused ? `▶ ${t('comments.resumeAi')}` : `⏸ ${t('comments.pauseAi')}`}
          </button>
          <span className="pcm-ai-status">
            {aiPaused ? t('comments.aiPausedLabel') : t('comments.aiActiveLabel')}
          </span>
        </div>

        {/* New Comment Input */}
        <div className="pcm-new-comment">
          <input
            className="pcm-reply-input"
            placeholder={t('comments.newCommentPlaceholder')}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlePostNewComment();
            }}
          />
          <button
            className="pcm-reply-btn"
            disabled={postingComment || !newCommentText.trim()}
            onClick={handlePostNewComment}
          >
            {postingComment ? '…' : t('comments.postAsOwner')}
          </button>
        </div>

        {/* Filter Chips */}
        <div className="pcm-filters">
          {filters.map((f) => (
            <button
              key={f.key}
              className={`pcm-filter-btn ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Comment List */}
        <div className="pcm-body">
          {loading ? (
            <div className="pcm-empty">{t('comments.loading')}</div>
          ) : comments.length === 0 ? (
            <div className="pcm-empty">
              <p>{t('comments.noComments')}</p>
              <p style={{ fontSize: '0.78rem' }}>{t('comments.noCommentsHint')}</p>
            </div>
          ) : (
            comments.map((c) => (
              <div
                key={c.id}
                className={`pcm-comment ${c.is_owner_comment ? 'owner-comment' : ''}`}
              >
                {/* ── Avatar + Content row ── */}
                <div className="pcm-comment-row">
                  <div className={`pcm-avatar ${c.is_owner_comment ? 'owner' : ''}`}>
                    {getInitial(c.external_username)}
                  </div>

                  <div className="pcm-comment-main">
                    {/* Header line */}
                    <div className="pcm-comment-header">
                      <span className="pcm-username">
                        {c.external_username}
                        {c.is_owner_comment && (
                          <span className="pcm-owner-badge">{t('comments.ownerBadge')}</span>
                        )}
                      </span>
                      {c.sentiment && (
                        <span className={`pcm-sentiment-badge ${c.sentiment}`}>
                          {t(`comments.sentiment.${c.sentiment}`)}
                        </span>
                      )}
                      <span className="pcm-time">{formatTime(c.created_at)}</span>
                    </div>

                    {/* Comment body — user message bubble (view or edit mode) */}
                    {editingId === c.id ? (
                      <div className="pcm-user-message-bubble editing">
                        <textarea
                          className="pcm-edit-textarea"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          rows={3}
                          autoFocus
                        />
                        <div className="pcm-edit-note">{t('comments.editNote')}</div>
                        <div className="pcm-edit-actions">
                          <button
                            className="pcm-reply-btn"
                            onClick={() => handleSaveEdit(c.id)}
                            disabled={savingEdit || !editText.trim()}
                          >
                            {savingEdit ? '…' : t('comments.saveEdit')}
                          </button>
                          <button
                            className="pcm-action-btn"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                          >
                            {t('comments.cancelEdit')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="pcm-user-message-bubble">
                        <div className="pcm-user-message-text">
                          {c.content || <span className="pcm-no-content">{t('comments.noContent')}</span>}
                        </div>
                        {c.like_count > 0 && (
                          <div className="pcm-likes">♥ {c.like_count}</div>
                        )}
                      </div>
                    )}

                    {/* Owner actions */}
                    {c.is_owner_comment && editingId !== c.id && (
                      <div className="pcm-owner-actions">
                        <button
                          className="pcm-action-btn"
                          onClick={() => startEdit(c)}
                          title={t('comments.editComment')}
                        >
                          {t('comments.editComment')}
                        </button>
                        <button
                          className="pcm-action-btn delete"
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c.id}
                          title={t('comments.deleteComment')}
                        >
                          {deletingId === c.id ? '…' : t('comments.deleteComment')}
                        </button>
                      </div>
                    )}

                    {/* Moderation action — non-owner public comments */}
                    {!c.is_owner_comment && (
                      <div className="pcm-moderate-action">
                        <button
                          className="pcm-action-btn moderate"
                          onClick={() => handleDelete(c)}
                          disabled={deletingId === c.id}
                          title={t('comments.moderateComment')}
                        >
                          {deletingId === c.id ? '…' : `🚫 ${t('comments.moderateComment')}`}
                        </button>
                      </div>
                    )}

                    {/* ── Threaded AI reply ── */}
                    {c.ai_response && (
                      <div className="pcm-ai-thread">
                        <div className="pcm-ai-reply-bubble">
                          <div className="pcm-avatar ai">🤖</div>
                          <div className="pcm-ai-reply-content">
                            <span className="pcm-ai-reply-author">
                              SmartGain AI
                              {c.ai_reply_external_id && (
                                <button
                                  className="pcm-action-btn delete"
                                  onClick={() => handleDeleteAiReply(c.id)}
                                  disabled={deletingAiReplyId === c.id}
                                  title={t('comments.deleteAiReply')}
                                >
                                  {deletingAiReplyId === c.id ? '…' : t('comments.deleteAiReply')}
                                </button>
                              )}
                            </span>
                            <div className="pcm-ai-reply-text">{c.ai_response}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Manual reply input */}
                    {!c.is_owner_comment && (
                      <div className="pcm-reply-row">
                        <input
                          className="pcm-reply-input"
                          placeholder={t('comments.replyPlaceholder')}
                          value={replyTexts[c.id] || ''}
                          onChange={(e) =>
                            setReplyTexts((prev) => ({
                              ...prev,
                              [c.id]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleReply(c.id);
                          }}
                        />
                        <button
                          className="pcm-reply-btn"
                          disabled={replying[c.id] || !(replyTexts[c.id] || '').trim()}
                          onClick={() => handleReply(c.id)}
                        >
                          {replying[c.id] ? '…' : t('comments.reply')}
                        </button>
                        {c.external_comment_id && (
                          <button
                            className="pcm-reply-btn"
                            onClick={() =>
                              setPrivateReplyTarget({
                                commentId: c.external_comment_id,
                                original: c.content || '',
                              })
                            }
                            title={t('privateReply.button')}
                          >
                            {t('privateReply.button')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>
      </div>

      {privateReplyTarget && (
        <PrivateReplyModal
          platform="instagram"
          commentId={privateReplyTarget.commentId}
          originalComment={privateReplyTarget.original}
          onClose={() => setPrivateReplyTarget(null)}
          onSent={() => setPrivateReplyTarget(null)}
        />
      )}
    </div>
  );
}

export default PostComments;
